import { useState, useCallback } from 'react';

/**
 * Comprehensive error handling hook with retry logic and user-friendly messages
 */
export const useErrorHandler = () => {
  const [error, setError] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);

  const handleError = useCallback((error, context = '') => {
    console.error(`Error in ${context}:`, error);
    
    // Extract user-friendly error message
    let userMessage = 'An unexpected error occurred. Please try again.';
    
    if (error?.message) {
      // Network errors
      if (error.message.includes('network') || error.message.includes('fetch')) {
        userMessage = 'Network error. Please check your connection and try again.';
      }
      // Firebase errors
      else if (error.message.includes('permission-denied')) {
        userMessage = 'You don\'t have permission to perform this action.';
      }
      else if (error.message.includes('not-found')) {
        userMessage = 'The requested item could not be found.';
      }
      else if (error.message.includes('already-exists')) {
        userMessage = 'This item already exists.';
      }
      // Validation errors
      else if (error.message.includes('invalid') || error.message.includes('required')) {
        userMessage = error.message;
      }
      // Generic
      else {
        userMessage = error.message;
      }
    }

    setError({
      message: userMessage,
      originalError: error,
      context,
      timestamp: new Date()
    });

    // Auto-clear error after 10 seconds
    setTimeout(() => setError(null), 10000);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const retry = useCallback(async (asyncFn, maxRetries = 3, delay = 1000) => {
    setIsRetrying(true);
    clearError();

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const result = await asyncFn();
        setIsRetrying(false);
        return { success: true, data: result };
      } catch (err) {
        if (attempt === maxRetries) {
          setIsRetrying(false);
          handleError(err, 'retry');
          return { success: false, error: err };
        }
        
        // Exponential backoff
        await new Promise(resolve => setTimeout(resolve, delay * attempt));
      }
    }
  }, [handleError, clearError]);

  return {
    error,
    isRetrying,
    handleError,
    clearError,
    retry
  };
};



